'use client';

import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateRight } from '@fortawesome/pro-regular-svg-icons';

import { StaticProviders } from './(public)/static-providers';
import { Navbar } from '@layouts/public-layout/navbar';
import { Footer } from '@layouts/public-layout/footer';
import { Icon, IconNames } from '@components/icon';
import { Button } from '@components/button';
import { Link } from '@components/link';
import { routes } from '@routes';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function ErrorPage({ error, reset }: ErrorProps) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <StaticProviders session={null}>
      <Navbar />
      <main id="main-content" className="min-h-[70vh]">
        <div className="flex flex-col items-center justify-center px-6 py-20 sm:py-28">
          <div className="max-w-lg text-center space-y-5">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-pool/50">
              <Icon
                name={IconNames.TRIANGLE_EXCLAMATION}
                className="text-primary-main text-2xl"
                aria-hidden="true"
              />
            </div>

            <p className="text-sm font-bold text-secondary-main uppercase tracking-[0.2em]">
              Something went wrong
            </p>

            <h1 className="text-4xl sm:text-5xl font-medium tracking-tight text-primary-main leading-tight">
              We hit a snag
            </h1>

            <p className="text-lg text-primary-main/60 max-w-sm mx-auto">
              An unexpected error occurred. Please try again, or reach out to
              our support team if the problem persists.
            </p>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link
              to={routes.contactSupport}
              isButton
              variant="outlined"
              size="lg"
            >
              Contact support
            </Link>

            <Button
              onClick={reset}
              size="lg"
              rightIcon={
                <FontAwesomeIcon
                  icon={faRotateRight}
                  className="w-4 h-4"
                  aria-hidden="true"
                />
              }
            >
              Try again
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </StaticProviders>
  );
}
